"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/design-system/utils/cn";
import { Maximize2, X } from "lucide-react";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import Mute from "~/components/icons/mute-eon";
import Pause from "~/components/icons/pause-eon";
import Play from "~/components/icons/play-eon";
import Volume from "~/components/icons/volume-eon";
import { env } from "~/setup/env";
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false
});
interface JobAdVideoProps {
  aspectRatio?: "16:9" | "1:1" | "9:16" | "4:3";
  buttonColor?: string;
  className?: string;
  expandable?: boolean;
  handleClick?: () => void;
  hideControls?: boolean;
  loop?: boolean;
  muted?: boolean;
  playOnLoad?: boolean;
  playsInline?: boolean;
  thumbnailSrc?: string;
  videoUrl?: string;
}
const ASPECT_RATIO_CLASSES = {
  "16:9": "aspect-video",
  "1:1": "aspect-square",
  "9:16": "aspect-[9/16]",
  "4:3": "aspect-[4/3]"
} as const;
const CONTROL_BUTTON_CLASS = "z-10 flex size-8 cursor-pointer select-none items-center justify-center rounded p-2 opacity-50 transition-opacity hover:opacity-80 active:opacity-90 md:size-12";
function resumeIfPaused(event: {
  currentTarget: EventTarget | null;
}) {
  const media = event.currentTarget;
  if (media instanceof HTMLMediaElement && media.paused) {
    media.play().catch(() => undefined);
  }
}
export default function JobAdVideo(t0) {
  const $ = _c(65);
  const {
    videoUrl,
    thumbnailSrc,
    className,
    aspectRatio: t1,
    loop: t2,
    playOnLoad: t3,
    buttonColor,
    expandable: t4,
    hideControls: t5,
    handleClick,
    playsInline: t6,
    muted: t7
  } = t0;
  const aspectRatio = t1 === undefined ? "16:9" : t1;
  const loop = t2 === undefined ? true : t2;
  const playOnLoad = t3 === undefined ? false : t3;
  const expandable = t4 === undefined ? false : t4;
  const hideControls = t5 === undefined ? false : t5;
  const playsInline = t6 === undefined ? true : t6;
  const muted = t7 === undefined ? true : t7;
  const [isMuted, setIsMuted] = useState(muted);
  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const t8 = buttonColor || "bg-primary";
  let t9;
  if ($[0] !== t8) {
    t9 = cn(t8, CONTROL_BUTTON_CLASS);
    $[0] = t8;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const controlClass = t9;
  let t10;
  let t11;
  if ($[2] !== playOnLoad) {
    t10 = () => {
      if (!playOnLoad) {
        setIsPlaying(false);
        return;
      }
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      setIsPlaying(isDesktop);
    };
    t11 = [playOnLoad];
    $[2] = playOnLoad;
    $[3] = t10;
    $[4] = t11;
  } else {
    t10 = $[3];
    t11 = $[4];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[5] !== expanded) {
    t12 = () => {
      if (!expanded) {
        return;
      }
      const onKey = event => {
        if (event.key === "Escape") {
          setExpanded(false);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t13 = [expanded];
    $[5] = expanded;
    $[6] = t12;
    $[7] = t13;
  } else {
    t12 = $[6];
    t13 = $[7];
  }
  useEffect(t12, t13);
  if (!videoUrl) {
    return null;
  }
  const videoSrc = videoUrl.startsWith("http") ? videoUrl : `${env.NEXT_PUBLIC_ASSET_URL}images/videos/${videoUrl}`;
  let t14;
  if ($[8] !== thumbnailSrc || $[9] !== videoUrl) {
    t14 = thumbnailSrc ? `${env.NEXT_PUBLIC_ASSET_URL}${thumbnailSrc}` : `${env.NEXT_PUBLIC_ASSET_URL}images/videos/${videoUrl.replace(".mp4", ".webp")}`;
    $[8] = thumbnailSrc;
    $[9] = videoUrl;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  const thumbnailUrl = t14;
  const t15 = ASPECT_RATIO_CLASSES[aspectRatio];
  let t16;
  if ($[11] !== className || $[12] !== t15) {
    t16 = cn("relative w-full print:hidden", t15, className);
    $[11] = className;
    $[12] = t15;
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  const t17 = `url(${thumbnailUrl})`;
  let t18;
  if ($[14] !== t17) {
    t18 = {
      backgroundImage: t17,
      backgroundSize: "contain"
    };
    $[14] = t17;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  let t19;
  if ($[16] !== expanded) {
    t19 = expanded && <button aria-label="Close video" className="fixed inset-0 z-[70] cursor-pointer bg-[#9DA5B0]/80" onClick={() => setExpanded(false)} type="button" />;
    $[16] = expanded;
    $[17] = t19;
  } else {
    t19 = $[17];
  }
  const t20 = expanded ? "fixed top-1/2 left-1/2 z-[80] aspect-video w-[min(56rem,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 bg-black" : "absolute inset-0";
  let t21;
  if ($[18] !== t20) {
    t21 = cn(t20);
    $[18] = t20;
    $[19] = t21;
  } else {
    t21 = $[19];
  }
  let t22;
  if ($[20] !== isMuted || $[21] !== isPlaying || $[22] !== loop || $[23] !== playsInline || $[24] !== videoSrc) {
    t22 = isPlaying && <ReactPlayer controls={false} height="100%" loop={loop} muted={isMuted} onCanPlay={resumeIfPaused} playing={isPlaying} playsInline={playsInline} preload="auto" src={videoSrc} volume={0.2} width="100%" />;
    $[20] = isMuted;
    $[21] = isPlaying;
    $[22] = loop;
    $[23] = playsInline;
    $[24] = videoSrc;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== controlClass || $[27] !== handleClick || $[28] !== hideControls || $[29] !== isPlaying) {
    t23 = isPlaying && !hideControls && <button aria-label="Pause" className={controlClass} onClick={() => {
      setIsPlaying(false);
      handleClick?.();
    }} type="button"><Pause className="size-6 text-white" /></button>;
    $[26] = controlClass;
    $[27] = handleClick;
    $[28] = hideControls;
    $[29] = isPlaying;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const t24 = isMuted ? "Unmute" : "Mute";
  const t25 = hideControls && "hidden";
  let t26;
  if ($[31] !== controlClass || $[32] !== t25) {
    t26 = cn(controlClass, t25);
    $[31] = controlClass;
    $[32] = t25;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== isMuted) {
    t27 = () => setIsMuted(!isMuted);
    $[34] = isMuted;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== isMuted) {
    t28 = isMuted ? <Mute className="size-6 text-white" /> : <Volume className="size-6 text-white" />;
    $[36] = isMuted;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== t24 || $[39] !== t26 || $[40] !== t27 || $[41] !== t28) {
    t29 = <button aria-label={t24} className={t26} onClick={t27} type="button">{t28}</button>;
    $[38] = t24;
    $[39] = t26;
    $[40] = t27;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== controlClass || $[44] !== expandable || $[45] !== expanded) {
    t30 = expandable && <button aria-label={expanded ? "Close video" : "Enlarge video"} className={controlClass} onClick={() => setExpanded(!expanded)} type="button">{expanded ? <X className="size-6 text-white" /> : <Maximize2 className="size-6 text-white" />}</button>;
    $[43] = controlClass;
    $[44] = expandable;
    $[45] = expanded;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] !== t23 || $[48] !== t29 || $[49] !== t30) {
    t31 = <div className="absolute top-2 right-2 flex gap-2">{t23}{t29}{t30}</div>;
    $[47] = t23;
    $[48] = t29;
    $[49] = t30;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== controlClass || $[52] !== handleClick || $[53] !== isPlaying) {
    t32 = !isPlaying && <button aria-label="Play video" className={cn(controlClass, "absolute top-1/2 left-1/2 size-20 -translate-x-1/2 -translate-y-1/2 rounded-lg opacity-60 md:size-20")} onClick={() => {
      setIsPlaying(true);
      handleClick?.();
    }} type="button"><Play className="size-12 text-white" /></button>;
    $[51] = controlClass;
    $[52] = handleClick;
    $[53] = isPlaying;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== t21 || $[56] !== t22 || $[57] !== t31 || $[58] !== t32) {
    t33 = <div className={t21}>{t22}{t31}{t32}</div>;
    $[55] = t21;
    $[56] = t22;
    $[57] = t31;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t16 || $[61] !== t18 || $[62] !== t19 || $[63] !== t33) {
    t34 = <div className={t16} id="video" style={t18}>{t19}{t33}</div>;
    $[60] = t16;
    $[61] = t18;
    $[62] = t19;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  return t34;
}